import React, { useState, useEffect, useCallback } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Box,
  IconButton,
  CircularProgress,
  LinearProgress,
  Button,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Close, OpenInNew } from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import { TYPE_OPTIONS_ENUM, TIME_PERIOD_VALUES, MODALITY_OPTIONS_LABELS } from '../../constants/observations';
import FacilityService from '../../services/facility.service';
import QualityObservationService from '../../services/quality-observation.service';
import ModalityMultiSelect from '../common/ModalityMultiSelect';
import SearchInput from '../common/SearchInput';
import StatusMultiSelect from '../common/StatusMultiSelect';
import TimePeriodSelector from '../common/TimePeriodSelector';

const useStyles = makeStyles((theme) => ({
  '& .MuiSvgIcon-root': {
    color: 'red !important',
  },
  '.MuiTable-root': {
    borderSpacing: 0,
  },
  '.MuiTableBody-root .MuiTableCell-root': {
    paddingBottom: '0 !important',
    paddingTop: '0 !important',
  },
  buttonsContainer: {
    display: 'flex',
    gap: 16,
    justifyContent: 'flex-end',
  },
  checkboxCell: {
    width: 50,
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  closeIcon: {
    '&:hover': {
      color: '#333',
      cursor: 'pointer',
    },
    color: '#aaa',
    display: 'none !important',
    padding: 2,
    position: 'absolute',
    right: 20,
    top: 4,
  },
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '8px',
      maxWidth: 960,
    },
  },
  dialogContent: {
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    padding: 0,
  },
  inputContainer: {
    '& .NativeDatepicker, .NativeDatepicker .MuiBox-root, & select': {
      height: 30,
      width: 140,
    },
    '&:hover>.MuiSvgIcon-fontSizeSmall': {
      display: 'block !important',
    },
    position: 'relative',
  },
  linkIconButton: {
    color: '#2AAAE2',
    padding: 4,
  },
  searchInputRoot: {
    '& .MuiInput-input': {
      height: 30,
      padding: '0 5px',
    },
    '& .MuiInput-root': {
      height: 30,
    },
    height: 30,
  },
  select: {
    '& .MuiInputBase-input': {
      color: '#999',
      fontFamily: 'Roboto',
      fontSize: 13,
      height: 30,
      padding: '0 5px',
    },
    '&:focus': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      outline: 'none',
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      cursor: 'pointer',
    },
    border: '1px solid #C8C8C8',
    borderRadius: 8,
    color: '#999',
    height: 30,
    overflow: 'hidden',
    width: 127,
  },
  table: {
    minWidth: 650,
  },
  tableBody: {
    borderSpacing: 0,
  },
  tableCell: {
    maxWidth: '200px',
    overflow: 'hidden',
    paddingBottom: '0 !important',
    paddingTop: '0 !important',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tableContainer: {
    height: '100%',
  },
  tableHeader: {
    borderBottom: '1px solid #EBEBEB',
  },
  tableHeaderCell: {
    backgroundColor: '#FFF',
    borderBottom: '1px solid #EBEBEB',
    fontSize: 12,
    fontWeight: 'bold',
  },
}));

const facilityService = new FacilityService();

const AddObservationToQuizModal = ({ open, onClose, onAddSelectedObservations, existingObservations = [] }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState(TYPE_OPTIONS_ENUM.ROUTINE);
  const [selectedModality, setSelectedModality] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [timePeriod, setTimePeriod] = useState(TIME_PERIOD_VALUES.ONE_MONTH);
  const [customDateRange, setCustomDateRange] = useState({
    endDate: moment().format('YYYY-MM-DD'),
    startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
  });

  const [observations, setObservations] = useState([]);
  const [selectedObservations, setSelectedObservations] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const ROWS_PER_PAGE = 10;

  const [isLoading, setIsLoading] = useState(false);
  const [loadingFacilities, setLoadingFacilities] = useState(false);

  const fetchFacilities = useCallback(async () => {
    try {
      setLoadingFacilities(true);

      const data = await facilityService.getAllFacilities();

      setFacilities(data || []);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      enqueueSnackbar('Failed to load facilities', { variant: 'error' });
    } finally {
      setLoadingFacilities(false);
    }
  }, [enqueueSnackbar]);

  const fetchObservations = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        limit: ROWS_PER_PAGE,
        page: page + 1,
        type: selectedType,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (selectedModality && selectedModality.length > 0) {
        params.modality = selectedModality.join(',');
      }
      if (selectedStatus && selectedStatus.length > 0) {
        params.status = selectedStatus.join(',');
      }
      if (selectedFacility) {
        params.facilityId = selectedFacility;
      }

      if (timePeriod === TIME_PERIOD_VALUES.CUSTOM) {
        params.startDate = customDateRange.startDate;
        params.endDate = customDateRange.endDate;
      }

      const data = await QualityObservationService.getAll(params);

      setObservations(data.items || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error('Error fetching observations:', error);
      enqueueSnackbar('Failed to load observations', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [page, ROWS_PER_PAGE, selectedType, searchTerm, selectedModality, selectedStatus, selectedFacility, timePeriod, customDateRange, enqueueSnackbar]);

  useEffect(() => {
    if (open) {
      fetchFacilities();
      setPage(0);
      fetchObservations();
    }
  }, [open, fetchFacilities]);

  useEffect(() => {
    if (open) {
      fetchObservations();
    }
  }, [fetchObservations, open]);

  useEffect(() => {
    if (observations.length > 0 && existingObservations.length > 0) {
      const existingIds = existingObservations.map((obs) => obs.qualityObservationId || obs.id);
      const matchingIds = observations.filter((obs) => existingIds.includes(obs.id)).map((obs) => obs.id);

      setSelectedObservations((previousValue) => {
        const newSelected = [...new Set([...previousValue, ...matchingIds])];
        return newSelected;
      });
    }
  }, [observations, existingObservations]);

  useEffect(() => {
    if (open) {
      const existingIds = existingObservations.map((obs) => obs.qualityObservationId || obs.id);
      setSelectedObservations(existingIds);
    }
  }, [open, existingObservations]);

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setPage(0);
  };

  const handleSearchChange = (searchValue) => {
    setSearchTerm(searchValue);
    setPage(0);
  };

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
    setPage(0);
  };

  const resetTypeFilter = () => {
    setSelectedType('');
    setPage(0);
  };

  const handleModalityChange = (value) => {
    setSelectedModality(value);
    setPage(0);
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    setPage(0);
  };

  const handleFacilityChange = (event) => {
    setSelectedFacility(event.target.value);
    setPage(0);
  };

  const resetFacilityFilter = () => {
    setSelectedFacility('');
    setPage(0);
  };

  const handleTimePeriodChange = (period, customRange) => {
    setTimePeriod(period);
    if (customRange) {
      setCustomDateRange(customRange);
    }
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSelectObservation = (id) => {
    setSelectedObservations((prev) => {
      if (prev.includes(id)) {
        return prev.filter((obsId) => obsId !== id);
      }
      return [...prev, id];
    });
  };

  const handleAddSelectedObservations = () => {
    // Get all selected observations from the current fetched list
    const selectedFromFetched = observations.filter((observation) => selectedObservations.includes(observation.id));

    // Get existing observations that are still selected
    const existingStillSelected = existingObservations.filter((existing) => {
      const existingId = existing.qualityObservationId || existing.id;
      return selectedObservations.includes(existingId);
    });

    // Combine both lists, avoiding duplicates
    const allSelectedObservations = [
      ...existingStillSelected,
      ...selectedFromFetched.filter((fetched) => {
        const existingIds = existingStillSelected.map((existing) => existing.qualityObservationId || existing.id);
        return !existingIds.includes(fetched.id);
      }),
    ];

    onAddSelectedObservations(allSelectedObservations);
    handleClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedType(TYPE_OPTIONS_ENUM.ROUTINE);
    setSelectedModality([]);
    setSelectedStatus([]);
    setSelectedFacility('');
    // Don't reset selectedObservations here - let it be managed by useEffect
    setObservations([]);
    setFacilities([]);
    onClose();
  };

  return (
    <Dialog
      className={classes.dialog}
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '8px',
          height: 'min(calc(100vh - 200px), 860px)',
          maxWidth: 960,
          minHeight: 600,
          padding: 30,
        },
      }}>
      <DialogTitle>
        Add Clinical Case
        <IconButton className={classes.closeButton} onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box width="100%" display="flex" flexDirection="column" gridGap={14} marginBottom={3}>
          <Box width="100%" display="flex" flexWrap="wrap" gridGap={10} alignItems="center">
            <SearchInput
              onSearch={handleSearch}
              onKeywordUpdate={handleSearchChange}
              placeholder="Search"
              disableSearch
              searchValue={searchTerm}
              classes={{
                root: classes.searchInputRoot,
              }}
            />

            <TimePeriodSelector timePeriod={timePeriod} onPeriodChange={handleTimePeriodChange} hideGranularity customDateRange={customDateRange} />
          </Box>

          <Box alignItems="center" width="100%" display="flex" flexWrap="wrap" gridGap={10}>
            <div className={classes.inputContainer}>
              <select className={classes.select} id="typeSelect" value={selectedType} onChange={handleTypeChange}>
                <option value="">Type</option>
                <option value={TYPE_OPTIONS_ENUM.ROUTINE}>Routine</option>
                <option value={TYPE_OPTIONS_ENUM.STAT}>STAT</option>
                <option value={TYPE_OPTIONS_ENUM.CALL_BACK}>Call Back</option>
              </select>
              {!!selectedType && <Close fontSize="small" className={classes.closeIcon} title="Reset Value" onClick={resetTypeFilter} />}
            </div>

            <div className={classes.inputContainer}>
              <select className={classes.select} id="facilitySelect" value={selectedFacility} onChange={handleFacilityChange}>
                <option value="">Facilities</option>
                {facilities.map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name}
                  </option>
                ))}
              </select>

              {loadingFacilities && <LinearProgress style={{ height: 2, width: '100%' }} />}
              {!!selectedFacility && <Close fontSize="small" className={classes.closeIcon} title="Reset Value" onClick={resetFacilityFilter} />}
            </div>

            <ModalityMultiSelect selectedModality={selectedModality} onChange={handleModalityChange} placeholder="Modalities" multiple />

            <StatusMultiSelect selectedStatus={selectedStatus} onChange={handleStatusChange} placeholder="Status" />
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table} stickyHeader>
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell className={`${classes.checkboxCell} ${classes.tableHeaderCell}`} />
                  <TableCell className={classes.tableHeaderCell}>ID</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Date</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Exam</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Facility</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Modality</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Educ.Value</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Severity</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Frequency</TableCell>
                  <TableCell className={classes.tableHeaderCell} width="40px" />
                </TableRow>
              </TableHead>

              <TableBody className={classes.tableBody}>
                {observations.map((observation) => (
                  <TableRow key={observation.id} hover>
                    <TableCell className={classes.tableCell} width="24px">
                      <Checkbox
                        style={{ color: selectedObservations.includes(observation.id) ? '#2AAAE2' : '#D6D6D6' }}
                        className={classes.checkbox}
                        checked={selectedObservations.includes(observation.id)}
                        onChange={() => handleSelectObservation(observation.id)}
                        title={`Observation Id: ${observation.id}`}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell} width="50px">
                      {observation.id || '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell} width="60px">
                      {observation.createdAt ? moment(observation.createdAt).format('MM/DD/YY') : '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>{observation.examName || '-'}</TableCell>
                    <TableCell className={classes.tableCell} maxWidth="200px">
                      {observation.facility?.name || '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell} width="60px">
                      {MODALITY_OPTIONS_LABELS[observation.modality] || '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell} width="40px" align="center">
                      {observation.scores?.educationalValue || '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell} width="40px" align="center">
                      {observation.scores?.severityOfIssue || '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell} width="40px" align="center">
                      {observation.scores?.frequencyOfIssue || '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell} width="40px" align="center">
                      <Tooltip title="Open in new tab">
                        <IconButton className={classes.linkIconButton} size="small" onClick={() => window.open(`/observation/${observation.id}`, '_blank')}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {observations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      {isLoading ? 'Loading observations...' : 'No observations found matching the current filters'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && totalCount > 1 && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2} mb={2}>
            <Pagination
              count={Math.ceil(totalCount / ROWS_PER_PAGE)}
              page={page + 1}
              onChange={(event, newPage) => handlePageChange(event, newPage - 1)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Box className={classes.buttonsContainer} width="100%">
          <Button style={{ width: '200px' }} onClick={handleClose} color="primary" variant="outlined">
            Close
          </Button>

          <Button style={{ width: '200px' }} onClick={handleAddSelectedObservations} color="primary" variant="contained" disabled={selectedObservations.length === 0 || isLoading}>
            Add ({selectedObservations.length})
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AddObservationToQuizModal;
